import React from 'react';
import { Box, Container, HStack, Image, Skeleton, SkeletonText, Stack, Text, VStack } from '@chakra-ui/react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { DONATE_PLACEMENTS, getDonationFormBySlug, getActiveSubscription, logger, type DonationForm as DonationFormModel } from '@kuow/core';
import { MonthlyUpgradeDonationForm, useDonateImpression } from '@kuow/shared-ui';

import niceCheckmark from '../assets/nice-checkmark.svg';
import { getAssetUrl, getImpactContent } from '../utils/donationFormTheme';

const isDonationFormUnavailable = (form: DonationFormModel): boolean => {
  return Boolean(form.archived) || form.status === 'draft';
};

const buildFallbackPath = (formSlug: string): string => {
  return formSlug ? `/donate/${formSlug}` : '/donate';
};

export const UpdateDonationPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const formSlug = (searchParams.get('slug') ?? slug ?? '').trim();
  const jwt = (searchParams.get('token') ?? '').trim();
  const utmCampaign = searchParams.get('utm_campaign');

  const [isLoading, setIsLoading] = React.useState(false);
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [formDetails, setFormDetails] = React.useState<DonationFormModel | null>(null);
  const formColumnRef = React.useRef<HTMLDivElement>(null);
  useDonateImpression(formColumnRef, DONATE_PLACEMENTS.upgrade);

  React.useEffect(() => {
    if (!formSlug || !jwt) return;

    let isMounted = true;
    setIsLoading(true);
    setShouldRedirect(false);
    setFormDetails(null);

    const load = async () => {
      try {
        const [details] = await Promise.all([
          getDonationFormBySlug(formSlug),
          getActiveSubscription(jwt),
        ]);
        if (!isMounted) return;
        if (isDonationFormUnavailable(details)) {
          setShouldRedirect(true);
          return;
        }
        setFormDetails(details);
      } catch (err) {
        logger.error('Failed to load update donation page; redirecting to fallback', { formSlug, error: err });
        if (!isMounted) return;
        setShouldRedirect(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [formSlug, jwt]);

  if (!formSlug || !jwt || shouldRedirect) {
    return <Navigate to={buildFallbackPath(formSlug)} replace />;
  }

  const backgroundImageUrl = getAssetUrl(formDetails?.background_image);
  const themeImageUrl = getAssetUrl(formDetails?.theme_image);
  const { impactHeadline, impactBullets, hasImpactSection } = getImpactContent(formDetails);

  return (
    <Box
      w="full"
      flex="1"
      display="flex"
      flexDirection="column"
      position="relative"
      backgroundImage={backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined}
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
      backgroundSize="cover"
      _before={
        backgroundImageUrl
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              bg: 'blackAlpha.600',
            }
          : undefined
      }
    >
      <Box flex="1" display="flex" flexDirection="column" justifyContent={{ base: 'flex-start', md: 'center' }} py={{ base: 10, md: 10 }}>
        <Container maxW="7xl" px={{ base: 6, md: 12 }} position="relative" zIndex={1}>
          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            align={{ base: 'center', md: 'flex-start' }}
            justify="center"
            gap={{ base: 10, md: 12 }}
          >
            <Box flex="1" color="fg.inverted" minW="0" maxW="lg">
              <VStack align="start" gap={{ base: 6, lg: 8 }} pt={{ base: 0, md: 12 }} pb={{ base: 10, md: 12 }}>
                <>
                  {isLoading ? (
                    <Box w="full">
                      <Skeleton h={11} borderRadius="md" />
                    </Box>
                  ) : themeImageUrl ? (
                    <Stack w="full" gap={{ base: 6, lg: 8 }} direction={{ base: 'column', lg: 'row' }} align={{ base: 'center', lg: 'end' }}>
                      <Box w="full" maxW={{ base: '56', lg: '64' }} aspectRatio="1" flexShrink={0}>
                        <Image src={themeImageUrl} alt={formDetails?.theme_image?.title ?? ''} w="full" h="full" objectFit="cover" />
                      </Box>
                      <Text as="h1" fontSize="4xl" fontWeight="semibold">
                        {formDetails?.headline ?? ''}
                      </Text>
                    </Stack>
                  ) : (
                    <Text as="h1" fontSize="4xl" fontWeight="semibold">
                      {formDetails?.headline ?? ''}
                    </Text>
                  )}

                  {isLoading ? (
                    <SkeletonText noOfLines={4} gap={3} w="full" />
                  ) : (
                    <Text fontSize="lg" opacity={0.9}>
                      {formDetails?.description ?? ''}
                    </Text>
                  )}

                  {!isLoading && hasImpactSection && (
                    <VStack gap={2.5} w="full" align="stretch" pt={2}>
                      {impactHeadline && (
                        <Text fontWeight="semibold" fontSize="md" textAlign="center">
                          {impactHeadline}
                        </Text>
                      )}

                      {impactBullets.map((bullet, idx) => (
                        <HStack key={`${idx}:${bullet}`} gap={2.5} align="center">
                          <Image src={String(niceCheckmark)} alt="" boxSize={6.5} />
                          <Text fontSize="sm" opacity={0.9} dangerouslySetInnerHTML={{ __html: bullet }}></Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </>
              </VStack>
            </Box>

            <Box ref={formColumnRef} flex="1" w={{ base: 'full', md: 'auto' }} minW={75} maxW="lg">
              <Box w="full" bg="bg" borderRadius="l3" px={8} py={8}>
                {isLoading ? (
                  <SkeletonText noOfLines={6} gap={3} />
                ) : (
                  <MonthlyUpgradeDonationForm formSlug={formSlug} jwt={jwt} utmCampaign={utmCampaign} placement={DONATE_PLACEMENTS.upgrade} />
                )}
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};


